import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { AppShell } from '../components/AppShell'
import { GradesTable } from '../components/journal/GradesTable'
import { GradeFormDialog } from '../components/journal/GradeFormDialog'
import { BulkGradeDialog } from '../components/journal/BulkGradeDialog'
import { useEntityList } from '../api/hooks/useEntity'
import { ENTITY, type Grade, type SchoolClass, type Student, type Subject } from '../types/entities'
import { lastNDates } from '../utils/gradeFormatters'
import { buildJournalExportRows, indexGradesByStudentAndDate } from '../utils/journal'
import { localIsoDate } from '../utils/dates'
import { accessLogApi } from '../api/accessLog'

async function exportGradesToExcel(
  students: Student[],
  dates: string[],
  gradesByCell: Map<string, Map<string, Grade[]>>,
  className: string,
  t: TFunction,
) {
  const XLSX = await import('xlsx')
  const ws = XLSX.utils.aoa_to_sheet(
    buildJournalExportRows(students, dates, gradesByCell, {
      student: t('journalPage.exportColStudent'),
      average: t('journalPage.exportColAverage'),
    }),
  )
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, t('journalPage.exportSheetName'))
  XLSX.writeFile(wb, `${t('journalPage.exportFilePrefix')}_${className}_${localIsoDate()}.xlsx`)
}

/**
 * Журнал оценок: таблица студенты × даты. Page-компонент отвечает только за
 * filter-state + загрузку сущностей + связь между таблицей и диалогами.
 * Сама таблица, ячейка и формы вынесены в `components/journal/`.
 */
export function JournalPage() {
  const { t } = useTranslation()
  const classes = useEntityList<SchoolClass>(ENTITY.schoolClass)
  const students = useEntityList<Student>(ENTITY.student)
  const subjects = useEntityList<Subject>(ENTITY.subject)
  const grades = useEntityList<Grade>(ENTITY.grade)

  const activeClasses = useMemo(
    () =>
      (classes.data ?? [])
        .filter((c) => c.archivedAt == null)
        .sort((a, b) => a.gradeLevel - b.gradeLevel || a.name.localeCompare(b.name)),
    [classes.data],
  )

  const [classId, setClassId] = useState<string>('')
  const [subjectId, setSubjectId] = useState<string>('')
  const [periodDays, setPeriodDays] = useState<number>(14)
  const [editing, setEditing] = useState<{ studentId: string; date: string } | null>(null)
  const [bulkOpen, setBulkOpen] = useState(false)

  // Auto-select первого класса один раз при загрузке
  useEffect(() => {
    if (!classId && activeClasses[0]) setClassId(activeClasses[0].id)
  }, [classId, activeClasses])

  const classStudents = useMemo(
    () =>
      (students.data ?? [])
        .filter((s) => s.classId === classId)
        .sort((a, b) =>
          a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName),
        ),
    [students.data, classId],
  )

  const classSubjects = useMemo(
    () => (subjects.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [subjects.data],
  )

  const dates = useMemo(() => lastNDates(periodDays), [periodDays])

  const gradesByCell = useMemo(
    () => indexGradesByStudentAndDate(grades.data ?? [], classId, subjectId),
    [grades.data, classId, subjectId],
  )

  if (activeClasses.length === 0) {
    return (
      <AppShell title={t('journalPage.title')}>
        <EmptyStateNoClasses />
      </AppShell>
    )
  }

  return (
    <AppShell
      title={t('journalPage.title')}
      subtitle={t('journalPage.subtitle', { students: classStudents.length, days: periodDays })}
      actions={
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => {
              exportGradesToExcel(
                classStudents,
                dates,
                gradesByCell,
                activeClasses.find((c) => c.id === classId)?.name ?? classId,
                t,
              )
              // 152-ФЗ: выгрузка оценок класса — в журнал доступа (только id)
              accessLogApi.reportExport(ENTITY.grade, classStudents.map((s) => s.id), classId)
            }}
            disabled={classStudents.length === 0}
            className="btn-outline text-sm"
          >
            {t('journalPage.exportExcel')}
          </button>
          <button
            onClick={() => setBulkOpen(true)}
            disabled={classStudents.length === 0}
            className="btn-outline text-sm"
          >
            {t('journalPage.bulkButton')}
          </button>
          <button
            onClick={() => setEditing({ studentId: '', date: dates[dates.length - 1] })}
            disabled={classStudents.length === 0}
            className="btn-primary text-sm"
          >
            {t('journalPage.addGrade')}
          </button>
        </div>
      }
    >
      <FilterBar
        activeClasses={activeClasses}
        classId={classId}
        onClassChange={setClassId}
        classSubjects={classSubjects}
        subjectId={subjectId}
        onSubjectChange={setSubjectId}
        periodDays={periodDays}
        onPeriodChange={setPeriodDays}
      />

      {classStudents.length === 0 ? (
        <EmptyStateNoStudents />
      ) : (
        <GradesTable
          students={classStudents}
          dates={dates}
          subjects={subjects.data ?? []}
          gradesByCell={gradesByCell}
          onCellClick={(studentId, date) => setEditing({ studentId, date })}
        />
      )}

      {editing && (
        <GradeFormDialog
          classId={classId}
          students={classStudents}
          subjects={classSubjects}
          preselectStudentId={editing.studentId}
          preselectDate={editing.date}
          onClose={() => setEditing(null)}
        />
      )}

      {bulkOpen && (
        <BulkGradeDialog
          classId={classId}
          students={classStudents}
          subjects={classSubjects}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </AppShell>
  )
}

function FilterBar({
  activeClasses,
  classId,
  onClassChange,
  classSubjects,
  subjectId,
  onSubjectChange,
  periodDays,
  onPeriodChange,
}: {
  activeClasses: SchoolClass[]
  classId: string
  onClassChange: (v: string) => void
  classSubjects: Subject[]
  subjectId: string
  onSubjectChange: (v: string) => void
  periodDays: number
  onPeriodChange: (v: number) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex gap-2 mb-5 flex-wrap items-center">
      <select className="input-field max-w-[180px]" value={classId} onChange={(e) => onClassChange(e.target.value)}>
        {activeClasses.map((c) => (
          <option key={c.id} value={c.id}>{c.name} ({c.gradeLevel})</option>
        ))}
      </select>
      <select
        className="input-field max-w-[200px]"
        value={subjectId}
        onChange={(e) => onSubjectChange(e.target.value)}
      >
        <option value="">{t('journalPage.allSubjects')}</option>
        {classSubjects.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
      <select
        className="input-field max-w-[140px]"
        value={periodDays}
        onChange={(e) => onPeriodChange(parseInt(e.target.value))}
      >
        <option value="7">{t('journalPage.period7')}</option>
        <option value="14">{t('journalPage.period14')}</option>
        <option value="30">{t('journalPage.period30')}</option>
        <option value="90">{t('journalPage.period90')}</option>
      </select>
    </div>
  )
}

function EmptyStateNoClasses() {
  const { t } = useTranslation()
  return (
    <div className="text-center py-16 bg-gradient-to-br from-primary-soft/30 to-white border border-purple-100/80 rounded-2xl">
      <div className="text-5xl mb-3">📊</div>
      <h3 className="font-semibold text-gray-900 mb-2">{t('journalPage.emptyNoClassesTitle')}</h3>
      <p className="text-sm text-gray-600 mb-4">{t('journalPage.emptyNoClassesText')}</p>
      <a href="/app/classes" className="btn-primary text-sm inline-block">{t('journalPage.toClasses')}</a>
    </div>
  )
}

function EmptyStateNoStudents() {
  const { t } = useTranslation()
  return (
    <div className="text-center py-16 bg-white border border-gray-200 rounded-2xl">
      <div className="text-4xl mb-3">👥</div>
      <h3 className="font-semibold text-gray-900 mb-2">{t('journalPage.emptyNoStudentsTitle')}</h3>
      <a href="/app/students" className="btn-primary text-sm inline-block">{t('journalPage.addStudent')}</a>
    </div>
  )
}
